import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { shareMetron } from "@/lib/share";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Mail, Lock, Eye, EyeOff, ArrowRight, Fingerprint, ShieldCheck, Loader2, Download, CheckCircle2, Share2, Share } from "lucide-react";
import { toast } from "sonner";

const BG = "https://static.prod-images.emergentagent.com/jobs/963b50f1-f77c-4e83-99db-abb6c6ad95ea/images/3cad815a8389e7905812a3a7d9147972f44378e5ac89bef2336b714c533d18ad.jpeg";

function MetronLogo() {
  return <img src="/metron-logo.png" alt="MÉTRON" translate="no" className="notranslate h-16 sm:h-20 w-auto object-contain drop-shadow-[0_10px_30px_rgba(73,215,220,0.28)]" />;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    setInstalled(standalone);
    const onPrompt = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const isIOS = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
  const instalarApp = async () => {
    if (installed) return; // já roda como PWA — não mostrar tutorial
    if (deferredPrompt) {
      // Android/desktop: prompt nativo de instalação (comportamento preservado)
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
    } else if (isIOS) {
      // iOS não instala automaticamente e não fornece prompt: abrir tutorial (sem share genérico)
      setIosHelp(true);
    } else {
      toast.info("Para instalar, use o menu do navegador e escolha 'Instalar app' / 'Adicionar à tela inicial'.", { duration: 7000 });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Falha no login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-[100dvh] w-full overflow-x-hidden" style={{ backgroundColor: "#0C1924" }}>
      {/* Fundo HD */}
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }} />
      {/* Véu de profundidade para leitura */}
      <div className="absolute inset-0" style={{ background: "radial-gradient(120% 70% at 50% 42%, rgba(12,25,36,0.30) 0%, rgba(12,25,36,0.62) 55%, rgba(12,25,36,0.86) 100%)" }} />

      <div className="relative z-10 min-h-[100dvh] flex flex-col justify-center px-6 max-w-md mx-auto w-full"
        style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))", paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        {/* Logo + identidade */}
        <div className="flex flex-col items-center">
          <MetronLogo />
          <h1 translate="no" className="notranslate font-heading font-bold tracking-[0.35em] pl-[0.35em] mt-1.5 text-4xl sm:text-5xl" style={{ color: "#F4F7FA" }}>MÉTRON</h1>
          <p className="text-xs sm:text-sm mt-1 text-center px-2" style={{ color: "#B8C5D0" }}>Plataforma Modular de Avaliação e Gestão de Competências</p>
        </div>

        {/* Formulário */}
        <form onSubmit={submit} className="mt-4 sm:mt-5 space-y-3">
          <div>
            <label htmlFor="email" className="block text-sm mb-1" style={{ color: "#F4F7FA" }}>E-mail</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "#49D7DC" }} />
              <input
                id="email" data-testid="login-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com"
                className="w-full h-12 rounded-2xl pl-12 pr-4 text-base outline-none transition-colors focus:border-[#49D7DC]"
                style={{ backgroundColor: "rgba(25,52,71,0.55)", border: "1px solid #3A5463", color: "#F4F7FA" }}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm mb-1" style={{ color: "#F4F7FA" }}>Senha</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "#49D7DC" }} />
              <input
                id="password" data-testid="login-password" type={showPass ? "text" : "password"} required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="Sua senha"
                className="w-full h-12 rounded-2xl pl-12 pr-12 text-base outline-none transition-colors focus:border-[#49D7DC]"
                style={{ backgroundColor: "rgba(25,52,71,0.55)", border: "1px solid #3A5463", color: "#F4F7FA" }}
              />
              <button type="button" data-testid="toggle-senha" onClick={() => setShowPass((s) => !s)}
                className="absolute right-4 top-1/2 -translate-y-1/2" style={{ color: "#B8C5D0" }}>
                {showPass ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <div className="text-right mt-1.5">
              <button type="button" data-testid="esqueci-senha" onClick={() => toast.info("Contate o administrador para redefinir sua senha.")}
                className="text-sm font-medium hover:underline" style={{ color: "#49D7DC" }}>
                Esqueci minha senha
              </button>
            </div>
          </div>

          {error && <p data-testid="login-error" className="text-sm" style={{ color: "#FF9B9B" }}>{error}</p>}

          <button type="submit" data-testid="login-submit" disabled={loading}
            className="w-full h-12 rounded-2xl flex items-center justify-center gap-2 font-semibold text-white text-lg transition-transform active:scale-[0.99] disabled:opacity-70"
            style={{ background: "linear-gradient(90deg, #1E7E86 0%, #29959A 45%, #49D7DC 100%)", boxShadow: "0 10px 30px rgba(41,149,154,0.35)" }}>
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Entrar <ArrowRight className="h-5 w-5" /></>}
          </button>

          {/* Biometria */}
          <button type="button" data-testid="login-biometria" onClick={() => toast.info("Acesso por biometria em breve.")}
            className="w-full h-12 rounded-2xl flex items-center justify-center gap-2.5 font-medium transition-colors"
            style={{ backgroundColor: "rgba(25,52,71,0.55)", border: "1px solid #3A5463", color: "#F4F7FA" }}>
            <Fingerprint className="h-5 w-5" style={{ color: "#49D7DC" }} /> Entrar com biometria
          </button>

          {/* Ações secundárias compactas — Instalar e Compartilhar são independentes */}
          <div className="grid grid-cols-2 gap-2">
            {installed ? (
              <div data-testid="pwa-instalado" className="h-11 rounded-2xl flex items-center justify-center gap-1.5 text-sm font-medium px-2"
                style={{ color: "#49D7DC", border: "1px solid #3A5463", backgroundColor: "rgba(25,52,71,0.35)" }}>
                <CheckCircle2 className="h-4 w-4 shrink-0" /> App instalado
              </div>
            ) : (
              <button type="button" data-testid="instalar-app-btn" onClick={instalarApp}
                className="h-11 rounded-2xl flex items-center justify-center gap-1.5 font-medium text-sm px-2 transition-colors"
                style={{ backgroundColor: "rgba(25,52,71,0.55)", border: "1px solid #3A5463", color: "#F4F7FA" }}>
                <Download className="h-4 w-4 shrink-0" style={{ color: "#49D7DC" }} /> Instalar app
              </button>
            )}
            <button type="button" data-testid="compartilhar-metron-btn" onClick={shareMetron}
              className="h-11 rounded-2xl flex items-center justify-center gap-1.5 font-medium text-sm px-2 transition-colors hover:opacity-90"
              style={{ backgroundColor: "transparent", border: "1px solid #3A5463", color: "#B8C5D0" }}>
              <Share2 className="h-4 w-4 shrink-0" style={{ color: "#49D7DC" }} /> Compartilhar
            </button>
          </div>

          <p className="text-center text-sm" style={{ color: "#B8C5D0" }}>
            Não tem uma conta?{" "}
            <a href="/registrar" data-testid="link-registrar" className="font-semibold hover:underline" style={{ color: "#49D7DC" }}>Criar conta</a>
          </p>
        </form>

        {/* Rodapé compacto */}
        <div className="mt-4 sm:mt-6 flex items-end justify-between gap-3">
          <div className="flex items-center gap-2" style={{ color: "#B8C5D0" }}>
            <ShieldCheck className="h-5 w-5 shrink-0" style={{ color: "#D3A46F" }} />
            <span className="text-xs leading-tight">Seus dados estão<br />sempre protegidos</span>
          </div>
          <div className="text-right text-[10px] tracking-[0.18em] uppercase leading-relaxed" style={{ color: "#8296A3" }}>
            Desenvolvendo pessoas<br />para um amanhã<br />mais competente
          </div>
        </div>
      </div>

      <Dialog open={iosHelp} onOpenChange={setIosHelp}>
        <DialogContent className="bg-popover border-border max-w-md" data-testid="ios-install-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" style={{ color: "#49D7DC" }} /> Instalar no iPhone
            </DialogTitle>
            <DialogDescription>
              O iOS não instala apps automaticamente. Siga os passos abaixo no Safari para adicionar o MÉTRON à Tela de Início.
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 mt-1 text-sm" style={{ color: "#F4F7FA" }} data-testid="ios-install-steps">
            {[
              <>Abra este endereço no <strong>Safari</strong> (o Chrome no iPhone não permite instalar PWAs).</>,
              <>Toque no botão <strong>Compartilhar</strong> <Share className="inline h-4 w-4 align-text-bottom" style={{ color: "#49D7DC" }} /> na barra do Safari.</>,
              <>Role e selecione <strong>Adicionar à Tela de Início</strong>.</>,
              <>Confirme tocando em <strong>Adicionar</strong>.</>,
            ].map((txt, i) => (
              <li key={i} className="flex gap-3 items-start">
                <span className="shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{ backgroundColor: "rgba(73,215,220,0.15)", color: "#49D7DC", border: "1px solid #3A5463" }}>{i + 1}</span>
                <span className="leading-snug">{txt}</span>
              </li>
            ))}
          </ol>
          <p className="text-xs leading-snug mt-1" style={{ color: "#B8C5D0" }}>
            No iOS 26, se aparecer a opção <strong>“Abrir como App”</strong>, mantenha-a <strong>ativada</strong> para abrir em tela cheia.
          </p>
          <button type="button" data-testid="ios-install-fechar" onClick={() => setIosHelp(false)}
            className="mt-2 w-full h-11 rounded-2xl flex items-center justify-center gap-1.5 font-medium text-sm transition-colors"
            style={{ backgroundColor: "rgba(25,52,71,0.55)", border: "1px solid #3A5463", color: "#F4F7FA" }}>
            Entendi
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
